import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { message } from 'antd';
import { useSelected } from '../../../../../providers/selectedContext';
import { useTheme } from '../../../../../providers/theme';
import { getColors } from '../../../../utils/getColors';
import { calculateSample, updateSample } from '../../../../../api/data';
import {
  CalculateSamplePayload,
  RegressionIndexes,
  UpdateSamplePayload
} from '../../../../../@types/samples/types';

import { 
  extractCurves, 
  RawDataPoint, 
  RegressionResult,
  ChartConfig
} from '../../logic';

import { generateRegressionData } from '../../logic';
import { calculateCoreyRegression } from '../../logic';
import { findOptimalSor } from '../../logic';
import { getChartOptions } from '../../logic';

export const useSwnKrwRegression = () => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { selectedInfo, setSelectedInfo } = useSelected();
  const sample = selectedInfo?.sample;

  const [rawDataTable, setRawDataTable] = useState<RawDataPoint[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  
  const [sor, setSor] = useState<number>(0);
  
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const lastSampleIdRef = useRef<string | undefined>(undefined);
  const resetPendingRef = useRef<boolean>(false);

  useEffect(() => {
    if (sample?.k_rel_values?.water_oil?.true_sor_w != null) {
      setSor(sample.k_rel_values.water_oil.true_sor_w);
    } else if (sample?.k_rel_values?.water_oil?.sor_w != null) {
      setSor(sample.k_rel_values.water_oil.sor_w);
    } else {
      setSor(0);
    }
  }, [sample]);

  useEffect(() => {
    setError(null);
    if (!sample) return;

    try {
      const { swArr, kArr, swi } = extractCurves(sample, 'water_oil_krw');

      if (swi == null || sor == null) {
           setRawDataTable([]);
           return;
      }
      
      const data = generateRegressionData(swArr, kArr, swi, sor, 'increasing');
      setRawDataTable(data);

    } catch (e: any) {
      setError(e.message);
      setRawDataTable([]);
    }
  }, [sample, sor]);

  useEffect(() => {
    if (!sample || rawDataTable.length === 0) return;

    const currentDataKeys = new Set(rawDataTable.map(d => d.key));
    const isNewSample = lastSampleIdRef.current !== sample.uid;
    
    lastSampleIdRef.current = sample.uid;

    setSelectedRowKeys((prevSelected) => {
      let candidates: any[] = [];

      if (isNewSample) {
        candidates = sample.regression_indexes?.swn_krw_selected_indexes || [];
        if (candidates.length === 0) return rawDataTable.map(d => d.key);
      } else {
        candidates = prevSelected;
        if (candidates.length === 0) candidates = sample.regression_indexes?.swn_krw_selected_indexes || [];
      }

      const validKeys = candidates.filter((k: any) => currentDataKeys.has(Number(k)));

      if (validKeys.length > 0) return validKeys;
      
      return rawDataTable.map(d => d.key);
    });
    
  }, [sample, rawDataTable]);

  const regressionResult = useMemo<RegressionResult | null>(() => {
    if (rawDataTable.length < 2 || selectedRowKeys.length < 2) {
      return null;
    }
    try {
      const indices = selectedRowKeys.map(key => Number(key));
      return calculateCoreyRegression(rawDataTable, indices);
    } catch (e) {
      return null; 
    }
  }, [rawDataTable, selectedRowKeys]);

  const handleSelectedRowKeysChange = useCallback((keys: React.Key[]) => {
    resetPendingRef.current = false;
    setSelectedRowKeys(keys);
  }, []);

  const handleReset = useCallback(() => {
    setSelectedRowKeys(rawDataTable.map(point => point.key));
    resetPendingRef.current = true;
  }, [rawDataTable]);

  const handleSave = useCallback(async () => {
    if (!sample) return;
    if (!regressionResult) {
        message.error("Cannot save: Invalid regression result.");
        return;
    }

    setLoading(true);
    setError(null);

    try {
      const indices = selectedRowKeys.map(key => Number(key));
      const existingIndexes = sample.regression_indexes || {};
      
      const indexesToSave: RegressionIndexes = {
        ...existingIndexes,
        swn_krw_selected_indexes: indices,
      };

      const payload: UpdateSamplePayload = {
        sample_uid: sample.uid,
        regression_indexes: resetPendingRef.current
          ? {
              ...existingIndexes,
              swn_krw_selected_indexes: [],
              refined_nw_selected_indexes: [],
            }
          : indexesToSave,
        true_krw: resetPendingRef.current ? null : regressionResult.a_max_calc
      };

      const updatedSample = await updateSample(payload);
      const refreshPayload: CalculateSamplePayload = {
        sample_uid: updatedSample.uid,
        strategies_keys: ['water-oil'],
        input_data_map: {
          'water-oil': { type: 'WATER_OIL' }
        }
      };
      const recalculatedSample = await calculateSample(refreshPayload);
      resetPendingRef.current = false;
      setSelectedInfo({ sample: recalculatedSample });
      message.success('Regression selection saved successfully!');
    } catch (e: any) {
      const saveError = e.message || 'Failed to save selection.';
      setError(saveError);
      message.warning(saveError);
    } finally {
      setLoading(false);
    }
  }, [sample, selectedRowKeys, regressionResult, sor, setSelectedInfo]);

  const chartOptions = useMemo(() => {
    
    const chartConfig: ChartConfig = {
        title: 'Swn vs Krw Regression',
        xAxisLabel: 'Swn (Normalized)',
        yAxisLabel: 'Krw (Calculated)',
        tooltipLabelX: 'Swn',
        tooltipLabelY: 'Krw',
        seriesNameRaw: 'All Lab Points (Normalized)',
        seriesNameSelected: 'Points in Regression',
        seriesNameFit: 'Krw Calc (Fit Line)',
        seriesColor: colors.waterOil, 
        fitLineColor: 'blue',
        xAxisReversed: false
    };

    return getChartOptions({
        regressionResult,
        rawDataTable,
        selectedRowKeys,
        config: chartConfig
    });
  }, [regressionResult, rawDataTable, selectedRowKeys, colors]);

  return {
    sample,
    sor,
    setSor,
    loading,
    error,
    rawDataTable,
    selectedRowKeys,
    setSelectedRowKeys: handleSelectedRowKeysChange,
    regressionResult,
    chartOptions,
    handleReset,
    handleSave,
    colors,
    isDay
  };
};
